import { lazy } from 'react';

/* ---------------------------------------------------------------------------
   React.lazy that survives one failed chunk request.

   Every route on this site is code-split, so opening a subpage means fetching
   a second JS file after the HTML. On a phone changing cells, in a tunnel, or
   on hotel wifi, that request is the single most likely thing to fail — and a
   rejected dynamic import throws straight through React, which unmounts the
   whole tree. The visible result is a blank page, which is exactly what a
   visitor reports as "it just goes white".

   One delayed retry turns the overwhelmingly common case (a single dropped
   request) into a short pause instead of a dead page. A second failure is
   treated as real and handed to RouteErrorBoundary, which can still offer a
   reload.

   `import()` results are cached by the browser per-module, but a REJECTED
   import is not cached, so calling the same factory again genuinely re-fetches.
--------------------------------------------------------------------------- */

export default function lazyWithRetry(factory, { retries = 1, delay = 600 } = {}) {
  return lazy(() =>
    factory().catch((error) => {
      if (retries <= 0) throw error;
      console.warn('[route] chunk failed to load, retrying once', error?.message || error);
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          factory().then(resolve, reject);
        }, delay);
      });
    })
  );
}
